// src/components/ContactForm.tsx
import React, { useState, useCallback } from 'react';

type FormStatus =
  | { type: 'idle' }
  | { type: 'submitting' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string };

const EMAIL_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_MESSAGE_CHARS = 1000;

export default function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<FormStatus>({ type: 'idle' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!name.trim()) {
      nextErrors.name = 'Please enter your name.';
    }

    if (!email.trim()) {
      nextErrors.email = 'Please enter your email address.';
    } else if (!EMAIL_REGEX.test(email.trim())) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!message.trim()) {
      nextErrors.message = 'Please enter a message.';
    } else if (message.trim().length < 10) {
      nextErrors.message = 'Message should be at least 10 characters.';
    } else if (message.trim().length > MAX_MESSAGE_CHARS) {
      nextErrors.message = `Message cannot exceed ${MAX_MESSAGE_CHARS} characters.`;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [name, email, message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // reset status
    setStatus({ type: 'idle' });

    if (!validate()) return;

    // honeypot: if filled, silently "succeed"
    if (website.trim().length > 0) {
      setStatus({
        type: 'success',
        message: 'Thanks for your message!',
      });
      // do NOT actually send anything
      return;
    }

    setStatus({ type: 'submitting' });

    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 15000);

      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          reason: reason.trim() || null,
          message: message.trim(),
          website: '', // honeypot should always be empty when real
        }),
      });

      window.clearTimeout(timeoutId);

      if (!res.ok) {
        const maybeJson = await res.json().catch(() => null);
        const errMessage =
          maybeJson?.error ||
          'Something went wrong while sending your message. Please try again in a moment.';

        setStatus({ type: 'error', message: errMessage });
        return;
      }

      setStatus({
        type: 'success',
        message: 'Thanks! Your message has been sent.',
      });

      setName('');
      setEmail('');
      setReason('');
      setMessage('');
      setErrors({});
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
      });
    }
  };

  const isSubmitting = status.type === 'submitting';

  return (
    <form
      onSubmit={handleSubmit}
      className="contact-form max-w-xl mx-auto space-y-6"
      noValidate
    >
      {/* Honeypot field (hidden from humans) */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <label>
          Website
          <input
            type="text"
            name="website"
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-2">
        <label htmlFor="contact-name" className="block text-sm font-medium">
          Name
        </label>
        <input
          id="contact-name"
          type="text"
          className="form-input"
          style={{ height: "3rem" }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSubmitting}
        />
        {errors.name && (
          <p className="text-xs text-red-600">{errors.name}</p>
        )}
      </div>

      <div className="space-y-2" style={{ marginTop: '12px' }}>
        <label htmlFor="contact-email" className="block text-sm font-medium">
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          className="form-input"
          style={{ height: "3rem" }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="text-xs text-red-600">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2" style={{ marginTop: '12px' }}>
        <label htmlFor="contact-reason" className="block text-sm font-medium">
          Reason (optional)
        </label>
        <select
          id="contact-reason"
          className="form-input"
          style={{ height: "3rem" }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isSubmitting}
        >
          <option value="">Choose a reason…</option>
          <option value="pre-sales">Pre-sales question</option>
          <option value="billing">Billing / licensing</option>
          <option value="bug">Bug report</option>
          <option value="feature">Feature request</option>
          <option value="other">Something else</option>
        </select>
      </div>

      <div className="space-y-2" style={{ marginTop: '12px' }}>
        <label htmlFor="contact-message" className="block text-sm font-medium">
          Message
        </label>
        <textarea
          id="contact-message"
          className="form-input"
          style={{ minHeight: "140px", padding: "0.75rem" }}
          maxLength={MAX_MESSAGE_CHARS}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSubmitting}
        />
        {errors.message && (
          <p className="text-xs text-red-600">{errors.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn btn-primary"
        style={{ marginTop: '12px' }}
      >
        {isSubmitting ? 'Sending…' : 'Send message'}
      </button>

      {status.type === 'success' && (
        <p className="text-sm text-green-700">{status.message}</p>
      )}

      {status.type === 'error' && (
        <p className="text-sm text-red-700">{status.message}</p>
      )}
    </form>
  );
}

export { ContactForm };
